import { useState, useEffect, useCallback } from 'react';
import {
  DefaultEntitySchema,
  EntityConfigurationData,
  DefaultSchemaField,
  Template,
  Overrides,
  Disabled
} from '../@types/Templates';
import { useTemplateEntityDefaults } from './useTemplateList';
import { useAxios } from '../context/AxiosContext';
import { useQuery } from 'react-query';

interface EntitiesList {
  entity: string[];
  injected: string[];
}

export const handleValues = (source: any[], data: any): DefaultSchemaField[] => {
  if (!data) return source;
  return source.map((el) => ({ ...el, value: data[el.key] || el.value || '' }));
};

const getEntityList = (data: any) => {
  return Object.entries(data).reduce(
    (a: EntitiesList, [key, value]: [string, any]) => {
      const { inject } = value;
      const result: EntitiesList = a;
      if (inject) result.injected = [...result.injected, ...inject];
      if (!result.injected.includes(key)) result.entity = [...result.entity, key];
      return result;
    },
    { entity: [], injected: [] }
  ).entity;
};

export function useDefaults(template: Template | undefined, onEntityChange: (template: any) => Promise<void | Error>) {
  const [entityList, setEntityList] = useState<string[]>([]);
  const [defaults, setDefaults] = useState<{ [x: string]: DefaultEntitySchema }>();
  const [overrides, setOverrides] = useState<Overrides>();
  const [disabled, setDisabled] = useState<Disabled>();
  const { data: entityDefaults }: any = useTemplateEntityDefaults();

  const getDefault = useCallback(
    (entity, fieldsValue = null) => {
      let { fields = [], inject } = entityDefaults![entity];
      if (inject) {
        return inject.reduce((a: any, b: any) => {
          return { ...a, ...getDefault(b, fieldsValue) };
        }, {});
      }
      if (fieldsValue) {
        fields = handleValues(fields, fieldsValue[entity]);
      }
      return { [entity]: fields };
    },
    [entityDefaults]
  );

  const processDefaults = useCallback(
    (data = null) => {
      return entityList.reduce((a, b) => {
        return { ...a, [b]: getDefault(b, data) };
      }, {});
    },
    [entityList, getDefault]
  );
  const benefitSponsorsGroupsFusion = useCallback((data = null) => {
    return {
      ...data,
      benefitSponsorsGroups: {
        ...data?.benefitSponsors,
        ...data?.groups
      }
    };
  }, []);

  const onDefaultsChange = useCallback(
    (entityName: string, data: EntityConfigurationData) => {
      setDefaults((state) => {
        return { ...state, [entityName]: getDefault(entityName, data) };
      });
      data.overrides = benefitSponsorsGroupsFusion(data.overrides);
      setOverrides(data.overrides);
      data.disabled = benefitSponsorsGroupsFusion(data.disabled);
      setDisabled(data.disabled);
      const updatedTemplate = { ...template, defaults: { ...template?.defaults, ...data } };
      return onEntityChange(updatedTemplate);
    },
    [getDefault, onEntityChange, template, benefitSponsorsGroupsFusion]
  );

  const setTemplateDefaults = useCallback(
    (templateDefaults) => {
      setDefaults(processDefaults(templateDefaults));
    },
    [processDefaults]
  );

  useEffect(() => {
    if (!defaults && entityDefaults) {
      setEntityList(getEntityList(entityDefaults));
    }
  }, [defaults, entityDefaults]);

  useEffect(() => {
    if (entityList.length) {
      setDefaults(processDefaults(template?.defaults));
      setOverrides(benefitSponsorsGroupsFusion(template?.defaults?.overrides));
      setDisabled(benefitSponsorsGroupsFusion(template?.defaults?.disabled));
    }
  }, [entityList, processDefaults, benefitSponsorsGroupsFusion, template]);

  return { defaults, overrides, disabled, onDefaultsChange, setTemplateDefaults };
}

export function useDefaultTemplateObject(onSuccess: (data: Template) => void) {
  const { authAxios } = useAxios();

  const fetchDefaultTemplateObject = useCallback(async (): Promise<Template> => {
    const response = await authAxios.get<Template>('/reseller/template/defaultObject');
    return response.data;
  }, [authAxios]);

  const { data } = useQuery(['defaultTemplateObject'], () => fetchDefaultTemplateObject(), {
    refetchOnWindowFocus: false,
    onSuccess
  });

  return { data };
}
